import React, { useEffect, useState, useMemo } from "react";
import SurveyListHeader from "../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../component/common/layout/SurveyListSidebar";
import AccountSettingSidebar from "../../../component/common/layout/AccountSettingSidebar";
import Card from "../../../core/Card";
import Input from "../../../core/Inputs/DefaultInput";
import SearchInput from "../../../core/Inputs/SearchInput";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import Svg from "../../../component/common/Svg";
import { Formik } from "formik";
import { toast } from "react-toastify";
import {
  createActivityTemplate,
  readActivityTemplates,
  updateActivityTemplate,
  deleteActivityTemplate,
} from "../../../store/actions/activityLogTemplateAction";
import style from "../../style.module.css";

const EMPTY_FORM = {
  templateCode: "",
  templateAction: "",
  templateText: "",
  dependencyArray: "",
};

export default function ActivityLogTemplate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activityTemplates } = useSelector(
    (state) => state.activityTemplateReducer
  );

  // ───────────────────────── pagination + search
  const [serverParams, setServerParams] = useState({
    startingLimit: 0,
    resultsPerPage: 8,
    search: "",
  });

  // ───────────────────────── modal state
  const [isModalOpen, setIsModalOpen] = useState(false); // add / edit
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState("");

  // ───────────────────────── delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);

  // ───────────────────────── load data
  useEffect(() => {
    dispatch(readActivityTemplates(serverParams));
  }, [dispatch, serverParams]);

  // ───────────────────────── delete handler
  const handleDeleteConfirm = () => {
    const payload = { activity_log_template_id: deleteTemplateId };
    dispatch(deleteActivityTemplate(payload))
      .then(() => {
        dispatch(readActivityTemplates(serverParams));
        toast.success(t("activityTemplate.delete_success"))
      })
      .catch((error) => {
        toast.error(t("activityTemplate.delete_failed") + error, {
          autoClose: 2000,
          theme: "colored",
          hideProgressBar: true,
          position: "bottom-right",
        });
      })
      .finally(() => {
        setShowDeleteModal(false);
        setDeleteTemplateId(null);
      });
  };

  const columns = useMemo(
    () => [
      {
        name: t("activityTemplate.TemplateAction"),
        selector: (row) => row.action,
         width: "200px",
        cell: (row) => <span>{row.action}</span>,
      },
        {
        name: t("activityTemplate.TemplateCode"),
        selector: (row) => row.template_code,
         width: "150px",
        cell: (row) => <span>{row.template_code}</span>,
      },
      {
        name: t("activityTemplate.TemplateText"),
        selector: (row) => row.text,
        // width: "400px",
        cell: (row) => <span>{row.text}</span>,
      },
    
      {
        name: t("activityTemplate.AddedBy"),
        selector: (row) => row.added_by_name,
         width: "150px",
        cell: (row) => <span>{row.added_by_name}</span>,
      },
      {
        name: t('survey.customVariable.action'),
         width: "150px",
        cell: (row) => (
          <div className="d-flex action-wrapper">
            {/* EDIT */}
            <a
              href="#"
              className="action-btn"
              onClick={(e) => {
                e.preventDefault();
                setModalMode("edit");
                setEditingId(row.activity_log_template_id);
                setFormValues({
                  templateCode: row.template_code,
                  templateAction: row.action,
                  templateText: row.text,
                  dependencyArray: row.dependency_array,
                });
                setIsModalOpen(true);
              }}
              title={t("common.edit")}
            >
             <Svg name="table-edit" />
            </a>
            {/* DELETE */}
            <a
              href="#"
              className="action-btn"
              onClick={(e) => {
                e.preventDefault();
                setDeleteTemplateId(row.activity_log_template_id);
                setShowDeleteModal(true);
              }}
              title={t("common.delete")}
            >
             <Svg name="table-delete" />
            </a>
          </div>
        ),
      },
    ],
    [t]
  );

  const filteredTemplates = useMemo(() => {
    if (!searchText.trim()) return activityTemplates;
    const kw = searchText.trim().toLowerCase();
    return activityTemplates.filter((tpl) =>
      [
        tpl.action,
        tpl.template_text,
        tpl.template_code,
        tpl.added_by_name,
      ]
        .join(" ")
        .toLowerCase()
        .includes(kw)
    );
  }, [activityTemplates, searchText]);

  return (
    <>
      <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
        <SurveyListHeader />
        <div id="viewport" className="d-flex bg-white">
          <SurveyListSidebar />
          <div className="flex-grow-1 d-flex">
            <AccountSettingSidebar />
            <div className="flex-grow-1 page-content-wrap d-flex flex-column">
              {/* Heading */}
              <div className="page-heading d-md-flex align-items-center">
                <div className="flex-grow-1">
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                      <li className="breadcrumb-item">
                        <a href="#">Home</a>
                      </li>
                      <li
                        className="breadcrumb-item active"
                        aria-current="page"
                      >
                        <strong>{t("activityTemplate.title")}</strong>
                      </li>
                    </ol>
                  </nav>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 ps-3 ps-lg-0">
                      <h1>{t("activityTemplate.title")}</h1>
                      <p className="pt-2 mb-0">{t("activityTemplate.desc")}</p>
                    </div>
                  </div>
                </div>
                <div className="button-wrap pt-3 pt-md-0">
                  <button
                     type="button"
                    className="btn btn-primary add-btn"
                    onClick={() => {
                      setModalMode("add");
                      setFormValues(EMPTY_FORM);
                      setIsModalOpen(true);
                    }}
                  >
                     <Svg name="plus" /> {t("activityTemplate.add")}
                  </button>
                </div>
              </div>

              {/* Search + Table */}
              <div className="role-wrapper flex-grow-1">
                <div className="table-wrapper-view">
                  <div className="search-filter d-md-flex align-items-center">
                    <div className="flex-grow-1 search-filter-padding">
                      <SearchInput
                        className="custom-search-input"
                        type="def"
                        view="outlined"
                        color="primary"
                        placeholder={t('settings.users_and_teams.users.search')}
                        fill={true}
                         value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                         // ⬇ optional: still tell the server too
                          setServerParams({
                            ...serverParams,
                            search: e.target.value,
                         });
                        }}
                        onClear={() => {
                          setSearchText("");
                          setServerParams({ ...serverParams, search: "" });
                        }}
                       
                      />
                    </div>
                  </div>
                  <div className="table-wrap custom-data-table">
                    <DataTable
                      columns={columns}
                      //data={activityTemplates}
                       data={filteredTemplates}
                      pagination
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {isModalOpen && (
        <TemplateModal
          mode={modalMode}
          initialValues={formValues}
          onClose={() => {
            setIsModalOpen(false);
            setFormValues(EMPTY_FORM);
            setEditingId(null);
          }}
          onSuccess={() => {
            dispatch(readActivityTemplates(serverParams));
            setIsModalOpen(false);
            setFormValues(EMPTY_FORM);
            setEditingId(null);
          }}
          t={t}
          dispatch={dispatch}
          isEdit={modalMode === "edit"}
          editingId={editingId}
        />
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <DeleteModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          t={t}
        />
      )}
    </>
  );
}


function TemplateModal({
  mode,
  initialValues,
  onClose,
  onSuccess,
  t,
  dispatch,
  isEdit,
  editingId,
}) {
  return (
    <div
      className="customvar-modal modal fade setting-modal show d-block"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-body">
            <div className="formcontainer">
              <div className="formvawrapper modal-setting-medium">
                <Card view="raised" className="formstyle p-4">
                  <div className="modal-heading">
                    <h4>
                      {mode === "add"
                        ? t("activityTemplate.add")
                        : `${t("common.edit")} ${t("activityTemplate.title")}`}
                    </h4>
                  </div>
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validate={(values) => {
                      const errors = {};
                      if (!values.templateCode)
                        errors.templateCode = t(
                          "activityLogTemplate.templateCodeRequired"
                        );
                      if (!values.templateAction)
                        errors.templateAction = t(
                          "activityLogTemplate.templateActionRequired"
                        );
                      if (!values.templateText)
                        errors.templateText = t(
                          "activityLogTemplate.templateTextRequired"
                        );
                      if (
                        !values.dependencyArray ||
                        values.dependencyArray.length === 0
                      )
                        errors.dependencyArray = t(
                          "activityLogTemplate.dependencyArrayRequired"
                        );
                      return errors;
                    }}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      const payload = {
                        template_code: values.templateCode,
                        action: values.templateAction,
                        text: values.templateText,
                        dependency_array: values.dependencyArray,
                        ...(isEdit && { activity_log_template_id: editingId }),
                      };

                      const thunk = isEdit
                        ? updateActivityTemplate
                        : createActivityTemplate;

                      dispatch(thunk(payload))
                        .then(() => {
                          toast.success(
                            mode === "add"
                              ? t("activityTemplate.create_success")
                              : t("activityTemplate.update_success"),
                          );
                          
                          resetForm();
                          onSuccess();
                        })
                        .catch(() => {
                          toast.error(
                            mode === "add"
                              ? t("activityTemplate.create_failed")
                              : t("activityTemplate.update_failed"),
  
                          );
                        })
                        .finally(() => setSubmitting(false));
                    }}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      values,
                      errors,
                      touched,
                      isSubmitting,
                    }) => (
                      <form onSubmit={handleSubmit} autoComplete="off">
                        <div className="form-wrapper input-margin-zero mt-3">
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="form-group mb-3 position-relative">
                                <label>
                                  {t("activityTemplate.TemplateCode")}
                                </label>
                                <Input
                                  type="text"
                                  view="outlined"
                                  name="templateCode"
                                  fill
                                  className="formsinput"
                                  placeholder={t(
                                    "activityTemplate.TemplateCodePlaceholder"
                                  )}
                                  onChange={handleChange}
                                  value={values.templateCode}
                                />
                                {touched.templateCode &&
                                  errors.templateCode && (
                                    <div className="validation-msg">
                                      {errors.templateCode}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="col-lg-12">
                              <div className="form-group mb-3 position-relative">
                                <label>
                                  {t("activityTemplate.TemplateAction")}
                                </label>
                                <Input
                                  type="text"
                                  view="outlined"
                                  name="templateAction"
                                  fill
                                  className="formsinput"
                                  placeholder={t(
                                    "activityTemplate.TemplateActionPlaceholder"
                                  )}
                                  onChange={handleChange}
                                  value={values.templateAction}
                                />
                                {touched.templateAction &&
                                  errors.templateAction && (
                                    <div className="validation-msg">
                                      {errors.templateAction}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="col-lg-12">
                              <div className="form-group mb-3 position-relative">
                                <label>
                                  {t("activityTemplate.TemplateText")}
                                </label>
                                <Input
                                  type="text"
                                  view="outlined"
                                  name="templateText"
                                  fill
                                  className="formsinput"
                                  placeholder={t(
                                    "activityTemplate.TemplateTextPlaceholder"
                                  )}
                                  onChange={handleChange}
                                  value={values.templateText}
                                />
                                {touched.templateText &&
                                  errors.templateText && (
                                    <div className="validation-msg">
                                      {errors.templateText}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="col-lg-12">
                              <div className="form-group mb-3 position-relative">
                                <label>
                                  {t("activityTemplate.DependencyArrayLabel")}
                                </label>
                                <Input
                                  type="text"
                                  view="outlined"
                                  fill
                                  name="dependencyArray"
                                  className="formsinput"
                                  placeholder={t(
                                    "activityTemplate.DependencyArrayPlaceholder"
                                  )}
                                  onChange={handleChange}
                                  value={values.dependencyArray}
                                />
                                {touched.dependencyArray &&
                                  errors.dependencyArray && (
                                    <div className="validation-msg">
                                      {errors.dependencyArray}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="button-wrap d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-bordered"
                            onClick={onClose}
                          >
                            {t('survey.customVariable.cancel')}
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary add-btn ms-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting
                              ? t(
                                  mode === "add"
                                    ? "submitting"
                                    : "updating"
                                )
                              : mode === "add"
                              ? t("survey.share.add")
                              : t("survey.share.update")}
                          </button>
                        </div>
                      </form>
                    )}
                  </Formik>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Delete Modal
function DeleteModal({ onClose, onConfirm, t }) {
  return (
    <div
      className="customvar-modal modal custom-modal fade deleteModal show d-block"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body">
            <div className="formcontainer">
              <div className="formvawrapper delete-modal-wrap modal-setting-medium">
                <Card view="raised" className="formstyle p-0">
                  <div className="delete-body-wrap delete-language">
                    <div className="heading">
                      <h3>Delete</h3>
                     Deleting this template will disable its triggers for all events it's associated with. This action cannot be undone. Are you sure?
                    </div>
                    <div className="button-wrap d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-bordered color-delete"
                        onClick={onClose}
                      >
                       {t('survey.customVariable.cancel')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-delete ms-3"
                        onClick={onConfirm}
                      >
                      {t('settings.roles.delete_action.delete')}
                      </button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// newewe